import React, { useEffect, useState, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { Shield, User, Bell, Key, UserX, LogIn } from 'lucide-react';
import api from '../utils/api';
import { PageHeader, Card, LoadingSpinner, Table, SearchBar } from '../components/UI';

const ACTION_ICONS = {
  LOGIN: { icon: LogIn, color: 'var(--success)' },
  LOGIN_FAILED: { icon: LogIn, color: 'var(--danger)' },
  LOGOUT: { icon: LogIn, color: 'var(--text-muted)' },
  PASSWORD_CHANGED: { icon: Key, color: 'var(--warning)' },
  PASSWORD_RESET: { icon: Key, color: 'var(--danger)' },
  USER_CREATED: { icon: User, color: 'var(--success)' },
  USER_UPDATED: { icon: User, color: 'var(--info)' },
  USER_DEACTIVATED: { icon: UserX, color: 'var(--danger)' },
  REMINDER_CREATED: { icon: Bell, color: 'var(--primary)' },
  REMINDER_UPDATED: { icon: Bell, color: 'var(--info)' },
  REMINDER_DELETED: { icon: Bell, color: 'var(--danger)' },
  REPORT_SUBMITTED: { icon: Bell, color: 'var(--success)' },
  PROFILE_UPDATED: { icon: User, color: 'var(--info)' },
};

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/logs/audit', { params: search ? { action: search } : {} });
      setLogs(res.data);
    } catch(e) {}
    setLoading(false);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const columns = [
    {
      header: 'Action', key: 'action',
      render: (v, row) => {
        const { icon: Icon, color } = ACTION_ICONS[v] || { icon: Shield, color: 'var(--text-muted)' };
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon size={13} color={color} />
            </div>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' }}>{v}</span>
          </div>
        );
      }
    },
    {
      header: 'User', key: 'full_name',
      render: (v, row) => (
        <div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{row.full_name || row.username || '—'}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.username}</div>
        </div>
      )
    },
    { header: 'Entity', key: 'entity_type', render: (v, row) => v ? <span style={{ fontSize: 12.5 }}>{v} #{row.entity_id}</span> : '—' },
    {
      header: 'Details', key: 'details',
      render: v => {
        try {
          const d = typeof v === 'string' ? JSON.parse(v) : v;
          const text = Object.entries(d || {}).map(([k, val]) => `${k}: ${val}`).join(' · ');
          return <span style={{ fontSize: 11.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{text || '—'}</span>;
        } catch { return '—'; }
      }
    },
    { header: 'IP', key: 'ip_address', render: v => <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>{v || '—'}</span> },
    { header: 'Timestamp', key: 'created_at', render: v => v ? <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)' }}>{format(parseISO(v), 'MMM d, yyyy h:mm a')}</span> : '—' },
  ];

  return (
    <div style={{ padding: '32px 36px', maxWidth: 1300, margin: '0 auto' }} className="fade-in">
      <PageHeader title="Audit Logs" subtitle="Complete trail of all system actions and security events" />

      <Card style={{ padding: '14px 18px', marginBottom: 16 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Filter by action (e.g. LOGIN, USER_CREATED)…" />
      </Card>

      <Card style={{ padding: 0 }}>
        {loading ? <LoadingSpinner /> : (
          <Table columns={columns} data={logs} emptyMessage="No audit log entries found." />
        )}
      </Card>
    </div>
  );
}
